import React, { useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Rocket, Loader2, CheckCircle2, RefreshCw } from "lucide-react";

/**
 * One-click admin button that runs every "first-time on production" data
 * sweep: refresh region-stock photos, force the Llyn Gwynant hand-picked
 * Wikimedia hero, and apply the heuristic year-round flagging across all
 * 1,191 campsites. Idempotent — safe to re-run any time.
 */
export default function DeployBootstrapCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (running) return;
    if (!confirm("Run the production data bootstrap? This will:\n\n  • Refresh all stock photos against the verified pool\n  • Apply the Llyn Gwynant hand-picked hero photo\n  • Flag year-round campsites via the heuristic sweep\n\nIt's safe to re-run any time.")) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/deploy-bootstrap");
      setResult(data);
      toast.success(`Done. ${data.year_round_flagged} new year-round flags applied.`, { duration: 5000 });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border-2 hairline-strong bg-gradient-to-br from-[#fff4ee] to-white p-6 mb-8" data-testid="deploy-bootstrap-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Rocket className="w-3 h-3" /> Production housekeeping
          </div>
          <h3 className="font-display text-xl mt-1">Run production data bootstrap</h3>
          <p className="text-sm text-[#444] leading-snug mt-2 max-w-[600px]">
            Applies the latest data updates from the preview environment to this database:
            stock photo refresh (preserves Wikimedia/user uploads), the Llyn Gwynant hand-picked
            hero photo, and the year-round heuristic sweep (uses 4 signals to flag bookable-today
            campsites). Idempotent — safe to re-run any time, especially after a fresh deploy
            or OSM ingest.
          </p>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="btn-primary text-sm px-6 py-3 inline-flex items-center gap-2 self-center disabled:opacity-50"
          data-testid="deploy-bootstrap-run"
        >
          {running ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
              Running…
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
              Run bootstrap
            </>
          )}
        </button>
      </div>

      {result && (
        <div className="mt-5 border hairline bg-white p-4" data-testid="deploy-bootstrap-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-[#22c55e]" strokeWidth={2.5} />
            <span className="font-display text-base">Bootstrap complete</span>
          </div>
          <ul className="text-sm space-y-1.5 mono text-[#333]">
            <li><strong>{result.stock_photos_refreshed === "deferred" ? "running in background…" : result.stock_photos_refreshed}</strong>{result.stock_photos_refreshed === "deferred" ? " stock photo refresh" : " stock photos refreshed"}</li>
            <li>Llyn Gwynant hero: <strong>{result.llyn_gwynant_hero}</strong></li>
            <li>
              {result.year_round_flagged === "deferred" ? (
                <>Year-round flagging: <strong>running in background…</strong></>
              ) : (
                <><strong>{result.year_round_flagged}</strong> new year-round campsites flagged</>
              )}
            </li>
            <li>Sitemap URLs: <strong>{result.sitemap_expected_urls?.toLocaleString()}</strong></li>
            {result.pitchup_sweep === "deferred" ? (
              <li>Pitchup sitemap sweep: <strong>running in background…</strong></li>
            ) : result.pitchup_sweep && (
              <li>
                Pitchup sweep: <strong>{result.pitchup_sweep.matched ?? 0}</strong> matched
                {", "}
                <strong>{result.pitchup_sweep.delisted ?? 0}</strong> delisted
              </li>
            )}
            {result.broken_links_sweep === "deferred" ? (
              <li>Broken-link sweep: <strong>running in background…</strong></li>
            ) : result.broken_links_sweep && (
              <li>
                Broken-link reports: <strong>{result.broken_links_sweep.newly_delisted ?? 0}</strong> newly delisted
                {", "}
                <strong>{result.broken_links_sweep.total_delisted ?? 0}</strong> total
              </li>
            )}
            {result.book_rank_backfill === "deferred" ? (
              <li>Book-rank backfill: <strong>running in background…</strong></li>
            ) : result.book_rank_backfill && (
              <li>
                Book-rank: <strong>{result.book_rank_backfill.rank_3_pitchup_ok ?? 0}</strong> Pitchup-ok · <strong>{result.book_rank_backfill.rank_2_alt_or_unknown ?? 0}</strong> direct-alt · <strong>{result.book_rank_backfill.rank_1_dead_with_alt ?? 0}</strong> dead-with-alt · <strong>{result.book_rank_backfill.rank_0_no_booking ?? 0}</strong> no-booking
              </li>
            )}
          </ul>
          <p className="text-[11px] mono text-[#888] mt-3">
            Background jobs typically finish in 2-3 minutes. Re-run bootstrap to see updated counts.
          </p>
          {result.summary && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mono">
              <Stat label="Total" value={result.summary.total_campsites} />
              <Stat label="Pitchup" value={result.summary.pitchup_listed} />
              <Stat label="Year-round" value={result.summary.year_round} />
              <Stat label="Pitchup Y/R" value={result.summary.pitchup_year_round} accent />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className={`border hairline p-3 ${accent ? "bg-[#fff4ee] border-[#ff4500]" : "bg-[#fafafa]"}`}>
    <div className="font-display text-xl">{(value ?? 0).toLocaleString()}</div>
    <div className="eyebrow text-[10px] !text-[#666]">{label}</div>
  </div>
);
